import { Check, XCircle } from "lucide-react";
import { inScopeServices, outOfScopeServices } from "../data/homePageContent";

// Scope entries end in a parenthetical form code, and the codes themselves contain spaces —
// "(1099-R, SSA-1099)". Left alone the line breaks inside the parenthetical and strands
// "SSA-1099)" by itself. text-wrap: pretty cannot help: every candidate break there is equally
// bad. So the whole trailing parenthetical is bound into one unbreakable unit, and the final
// space of the entry is bound too, which guarantees at least two words on the closing line.
const NBSP = "\u00a0";
// Bound only while the result still fits a phone. Two entries end in prose rather than a form
// code — "(needs your broker's supplemental statement)" and "(FBAR, FATCA, Forms 2555 and 1116)" —
// and binding those produced unbreakable runs measured at 385px and 345px inside a 343px card,
// which pushed the whole page into a horizontal scroll on a real device. The limit is measured
// rather than guessed: 28 characters keeps every genuine form code bound, including the
// "(1099-R, SSA-1099)" case this exists for, and releases exactly the entries that do not fit.
const MAX_BOUND_TAIL = 28;

const bindTail = (label: string) => {
  const bound = label
    .replace(/ \(([^()]*)\)$/, (_, code: string) => `${NBSP}(${code.replace(/ /g, NBSP)})`)
    .replace(/ (\S+)$/, `${NBSP}$1`);
  // What has to fit is the unbreakable run: everything after the last ordinary space.
  const tail = bound.slice(bound.lastIndexOf(" ") + 1);
  return tail.length > MAX_BOUND_TAIL ? label : bound;
};

export function WhoWeServe() {
  return (
    // Heading on top, grid full width below — the same shape as the features section. Beside the
    // grid the heading column left a tall void and squeezed the lists into three-line wraps.
    <section className="section-band section-band--paper" id="scope">
      <div className="site-shell">
        <div className="section-heading reveal">
          <p className="kicker">Scope &amp; eligibility</p>
          <h2>Exclusively for California full-year residents.</h2>
          <p className="section-lede">
            We prepare returns your own documents support. What we send elsewhere is work that
            depends on records a preparer has to build and carry forward year after year. If you
            are not sure which side you fall on, ask us before you start.
          </p>
        </div>
        <div className="scope-boxes reveal reveal--delay">
          <div className="scope-box scope-box--in">
            <h3 className="scope-box__title">
              <span className="scope-box__badge scope-box__badge--in">Supported</span>
              Personal tax returns in California
            </h3>
            <ul className="scope-list">
              {inScopeServices.map((item) => (
                <li key={item}>
                  <Check className="scope-list__icon scope-list__icon--in" size={17} strokeWidth={2.4} aria-hidden="true" />
                  <span>{bindTail(item)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="scope-box scope-box--out">
            <h3 className="scope-box__title">
              <span className="scope-box__badge scope-box__badge--out">Out of scope</span>
              Referred out, not prepared here
            </h3>
            <ul className="scope-list">
              {outOfScopeServices.map((item) => (
                <li key={item}>
                  <XCircle className="scope-list__icon scope-list__icon--out" size={17} strokeWidth={2} aria-hidden="true" />
                  <span>{bindTail(item)}</span>
                </li>
              ))}
            </ul>
            {/* The supported column is more than twice as long. Rather than leave the shorter box
                padded with dead space, close it with the sentence the section's promise implies. */}
            <p className="scope-box__note">
              Not sure where your return lands? Ask before you start &mdash; we will tell you
              plainly, and it costs nothing to check.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
